import { FieldValues, get } from 'react-hook-form';

import { DEFAULT_FORMAT, formatDate, isDefined, isEmpty, isNotDefined, isNotEmpty, isOfType } from '@hsp/utilities';

import { IAttributeDataPoint } from '@t/api/audience/form/attribute.data-point';
import { ATTRIBUTES_FORM_NAMES, AttributesFormNameKeys } from '@t/api/audience/form/attribute-names.type';

import { MiscellaneousFormFieldsName } from 'pages/audience/shared';
import { filterFilterValues } from 'pages/audience/utilities/filter-filter-values';
import { IAbOrder } from 'types/api/ab-order';
import {
  AudienceSearchCondition,
  DataSourceFormNameKeys,
  FilterType,
  IAudience,
  IAudienceCategorySearch,
  IAudienceSearch,
  IAudienceSearchConditions,
  IFormCategory,
  IMasterFile,
  IOmitFlag,
  ISearchFormValue,
  ISearchParameter,
  ISearchSubParameter,
  ISelectedDistrictFilter,
  ISelectedFilter,
  SearchType,
} from 'types/api/audience';
import { IClient } from 'types/api/client';
import { IClientList } from 'types/api/client-list';
import { IDistrict } from 'types/api/districts';
import { IExternalList } from 'types/api/external-list';
import { IHouseOrder } from 'types/api/house-order';
import { IListRental } from 'types/api/list-rental';
import { IMailCode } from 'types/api/mail-codes';
import { IState } from 'types/api/states';
import { ITag } from 'types/api/tag';
import { IClientListReturn } from 'types/pages/audience-builder/shared/hooks/client-lists';

const appendSearchParameter = (
  categoryJson: IAudienceCategorySearch,
  parameter: ISearchParameter | ISearchSubParameter
) => {
  return {
    ...categoryJson,
    and: [...categoryJson.and, parameter],
  };
};

const getMinMax = (value: string) => {
  if (isEmpty(value)) {
    return;
  }

  const [min, max] = value.split(',');

  if (isEmpty(min) && isEmpty(max)) {
    return;
  }

  return [min, max] as const;
};

const processConditions = (conditions: AudienceSearchCondition[] = []) => {
  let parameters: ISearchParameter[] = [];
  let miscellaneous: FieldValues = {};

  for (const condition of conditions) {
    const { category, dataPoint, ...rest } = condition;

    if (isNotDefined(dataPoint)) {
      continue;
    }

    const keys = Object.keys(rest);

    if ((dataPoint.formName ?? '').indexOf(MiscellaneousFormFieldsName) < 0) {
      for (const key of keys) {
        const formValues = condition[key];
        const names = Object.keys(formValues);

        for (const name of names) {
          const parameter: ISearchParameter = {
            ...formValues[name],
            name,
          };

          parameters = [...parameters, parameter];
        }
      }
    } else {
      const keys = Object.keys(rest);

      for (const key of keys) {
        miscellaneous = {
          ...miscellaneous,
          ...rest[key],
        };
      }
    }
  }

  return {
    miscellaneous,
    parameters,
  };
};

const generateAttributesParameters = (search: IAudienceSearch, fieldValues: FieldValues) => {
  const keys = AttributesFormNameKeys;

  const formValues = get(fieldValues, SearchType.Attributes);

  if (isNotDefined(formValues)) {
    return;
  }

  for (const key of keys) {
    const formValue: IAttributeDataPoint = get(formValues, key);

    if (isNotDefined(formValue)) {
      continue;
    }

    const { formName, value } = formValue;

    if (formName === ATTRIBUTES_FORM_NAMES.ExcludeBusiness) {
      search = Object.assign(search, { [formName]: value?.toString() == '1' ? true : false });
      continue;
    }

    search = Object.assign(search, { [formName]: value });
  }
};

const generateBasicSearchParameters = (
  search: IAudienceSearch,
  fieldValues: FieldValues,
  categories?: IFormCategory[]
) => {
  if (isNotDefined(fieldValues[SearchType.Basic])) {
    return;
  }
  const { [MiscellaneousFormFieldsName]: misc, ...formValues } = fieldValues[SearchType.Basic];
  const keys = Object.keys(formValues);
  let categoryJson: IAudienceCategorySearch = { and: [], or: [], step: '1' };

  for (const key of keys) {
    const formValue = formValues[key];
    const names = Object.keys(formValue);

    for (const name of names) {
      if (isNotDefined(formValue[name])) {
        continue;
      }

      const { operator, type, value } = formValue[name] as ISearchFormValue;

      if (isDefined(categories)) {
        if (key !== 'RFM' && isNotEmpty(value)) {
          let source = '';

          categories.forEach((item) => {
            if (item.category === key) {
              source = item.source;
            }
          });
          if (isNotDefined(search['autoTag']) || search['autoTag'].length === 0) {
            search['autoTag'] = [source + '.' + key + '.' + name];
          } else {
            search['autoTag'] = [...search['autoTag'], source + '.' + key + '.' + name];
          }
        }
      }

      switch (type) {
        case 'date-range': {
          const tuple = getMinMax(value);

          if (isNotDefined(tuple)) {
            break;
          }

          const [min, max] = tuple;

          if (isNotEmpty(min)) {
            if (isDefined(min)) {
              categoryJson = appendSearchParameter(categoryJson, {
                name: `${name}_low`,
                operator,
                type,
                value: isEmpty(min) ? '' : formatDate(min, { format: 'yyyy-MM-dd', parseFormat: DEFAULT_FORMAT }),
              });
            }
          }

          if (isNotEmpty(max)) {
            if (isDefined(max)) {
              categoryJson = appendSearchParameter(categoryJson, {
                name: `${name}_high`,
                operator,
                type,
                value: isEmpty(max) ? '' : formatDate(max, { format: 'yyyy-MM-dd', parseFormat: DEFAULT_FORMAT }),
              });
            }
          }

          break;
        }

        case 'dollar-type': {
          if (isNotEmpty(value)) {
            categoryJson = appendSearchParameter(categoryJson, {
              name,
              operator,
              type,
              value: value === 'Amount' ? '' : value,
            });
          }
          break;
        }

        case 'multiselect': {
          if (isNotEmpty(value)) {
            categoryJson = appendSearchParameter(categoryJson, {
              name,
              operator,
              type,
              value: `(${value})`,
            });
          }
          break;
        }

        case 'number':

        case 'range':

        case 'slider': {
          const tuple = getMinMax(value);

          if (isNotDefined(tuple)) {
            break;
          }

          const [min, max] = tuple;

          if (isEmpty(max)) {
            categoryJson = appendSearchParameter(categoryJson, {
              name: type === 'range' ? `${name}_low` : name,
              operator: '>=',
              type,
              value: min,
            });
          } else if (isEmpty(min)) {
            categoryJson = appendSearchParameter(categoryJson, {
              name: type === 'range' ? `${name}_high` : name,
              operator: '<=',
              type,
              value: max,
            });
          } else {
            categoryJson = appendSearchParameter(categoryJson, {
              sub: {
                and: [
                  {
                    name: type === 'range' ? `${name}_low` : name,
                    operator: '>=',
                    type,
                    value: min,
                  },
                  {
                    name: type === 'range' ? `${name}_high` : name,
                    operator: '<=',
                    type,
                    value: max,
                  },
                ],
              },
            });
          }
          break;
        }

        case 'recency': {
          // this is for advanced parameters. Need to revisit if ever enabled.
          // if (!value.includes(',')) {
          //   if (isNotEmpty(value)) {
          //     categoryJson = appendSearchParameter(categoryJson, {
          //       name,
          //       operator,
          //       type,
          //       value: value,
          //     });
          //   }
          //   break;
          // }

          const tuple = getMinMax(value);

          if (isNotDefined(tuple)) {
            break;
          }

          const [min, max] = tuple;

          if (min === '0' && max === '0') {
            break;
          }

          if (isDefined(min)) {
            categoryJson = appendSearchParameter(categoryJson, {
              name: `${name}_low`,
              operator,
              type,
              value: isEmpty(min) ? '0' : min,
            });
          }

          if (isDefined(max)) {
            if (isEmpty(max)) {
              break;
            }
            categoryJson = appendSearchParameter(categoryJson, {
              name: `${name}_high`,
              operator,
              type,
              value: max,
            });
          }

          break;
        }

        default: {
          if (isNotEmpty(value)) {
            categoryJson = appendSearchParameter(categoryJson, {
              name,
              operator,
              type,
              value,
            });
          }
        }
      }
    }
  }

  generateMiscellaneousSearchParameters(search, misc);

  search.categoryJson = JSON.stringify([categoryJson]);
};

const generateConditionsSearchParameters = (search: IAudienceSearch, fieldValues: FieldValues) => {
  if (isNotDefined(fieldValues[SearchType.Conditions])) {
    return;
  }
  const { and, or } = fieldValues[SearchType.Conditions] as IAudienceSearchConditions;
  const { miscellaneous: andMiscellaneous, parameters: andParameters } = processConditions(and);
  const { miscellaneous: orMiscellaneous, parameters: orParameters } = processConditions(or);

  search.categoryJson = JSON.stringify([
    {
      and: andParameters,
      or: orParameters,
      step: '1',
    },
  ]);

  generateMiscellaneousSearchParameters(search, { ...andMiscellaneous, ...orMiscellaneous });
};

const generateDataSourceParameters = (
  search: IAudienceSearch,
  values: FieldValues,
  { excludeInclude, use }: IClientListReturn
) => {
  const keys = DataSourceFormNameKeys;
  const formValues = get(values, SearchType.DataSource);

  if (isNotDefined(formValues)) {
    return;
  }

  for (const key of keys) {
    const formValue = get(formValues, key);

    if (isNotDefined(formValue) || isEmpty(formValue)) {
      continue;
    }

    switch (key) {
      case 'clients': {
        const selectedSource = formValue as ISelectedFilter<IClient>;

        if (isNotDefined(selectedSource) || isEmpty(selectedSource)) {
          break;
        }

        const { filters, type } = selectedSource;

        if (isNotDefined(filters)) {
          return;
        }

        let property: keyof IAudienceSearch;

        switch (type) {
          case 'exclude':
            property = 'excludeClients';
            break;

          case 'include':
            property = 'includeClients';
            break;
        }

        if (isNotEmpty(filters)) {
          search[property] = filters;
        }
        break;
      }

      case 'external-lists': {
        const externalList = formValue as ISelectedFilter<IExternalList>;

        if (isNotDefined(externalList)) {
          break;
        }

        const { filters } = externalList;

        if (isNotDefined(filters)) {
          return;
        }

        const property: keyof IAudienceSearch = 'externalLists';

        if (isNotEmpty(filters)) {
          search[property] = filters;
        }
        break;
      }

      case 'lists': {
        const selectedSource = formValue as ISelectedFilter<IClientList>;

        if (isNotDefined(selectedSource) || isEmpty(selectedSource)) {
          break;
        }

        const { filters, type } = selectedSource;

        if (isNotDefined(filters) || isNotDefined(type)) {
          return;
        }

        let property: keyof IAudienceSearch;

        switch (type) {
          case 'exclude': {
            property = 'excludeClientLists';
            break;
          }

          case 'include':
            property = 'includeClientLists';
            break;
        }

        if (isNotEmpty(excludeInclude)) {
          search[property] = excludeInclude;
        }

        if (isNotEmpty(use)) {
          search.useClientLists = use;
        }
        break;
      }

      case 'master-file': {
        const source = formValue as IAudience | IMasterFile;

        if (isNotDefined(source) || isEmpty(source)) {
          break;
        }

        if (isOfType<IMasterFile>(source, 'listId')) {
          const property: keyof IAudienceSearch = 'includeMasterLists';

          search[property] = [source];
        }

        break;
      }

      case 'third-party': {
        const source = formValue as string;

        if (isNotDefined(source) || isEmpty(source)) {
          break;
        }

        const property: keyof IAudienceSearch = 'dataSource';

        search[property] = source;
        break;
      }
    }
  }
};

const generateFilterParameters = (search: IAudienceSearch, values: FieldValues) => {
  const keys = filterFilterValues();

  keys.push(SearchType.Tags);

  for (const key of keys) {
    switch (key) {
      case SearchType.AbOrder: {
        processFilter<IAbOrder>(search, 'excludePrevABOrder', values, key);
        break;
      }

      case SearchType.CongressionalDistricts: {
        const selectedFilter = values[key] as ISelectedDistrictFilter;

        if (isNotDefined(selectedFilter) || isEmpty(selectedFilter)) {
          break;
        }

        const { filters, type } = selectedFilter;
        let property: keyof IAudienceSearch;

        switch (type) {
          case 'exclude':
            property = 'excludeDistrict';
            break;

          case 'include':
            property = 'includeDistrict';
            break;
        }

        const states = Object.keys(filters);

        const parameters = states
          .reduce((previous, current) => {
            const { all, selected } = filters[current];
            const transformDistricts = (districts: IDistrict[]) => {
              return districts?.map(({ district }) => `${current};${district}`) ?? [];
            };
            const concatenated = transformDistricts(isDefined(selected) && isNotEmpty(selected) ? selected : all);

            return [...previous, ...concatenated];
          }, [] as string[])
          .join('|');

        if (isNotEmpty(parameters)) {
          search[property] = parameters;
        }
        break;
      }

      case SearchType.Locations: {
        const selectedFilter = values[key];

        if (isNotDefined(selectedFilter) || isEmpty(selectedFilter)) {
          break;
        }

        const scf = selectedFilter.scf as ISelectedFilter;
        const states = selectedFilter.states as ISelectedFilter<IState>;
        const zips = selectedFilter.zips as ISelectedFilter;
        let property: keyof IAudienceSearch;

        if (isDefined(states) && isNotEmpty(states)) {
          const { type } = states;

          switch (type) {
            case 'exclude':
              property = 'excludeStates';
              break;

            case 'include':
              property = 'includeStates';
              break;
          }

          processFilter<IState>(search, property, values[key], 'states');
        }

        if (isDefined(zips) && isNotEmpty(zips)) {
          const { type } = zips;

          switch (type) {
            case 'exclude':
              property = 'excludeZip';
              break;

            case 'include':
              property = 'includeZip';
              break;
          }

          processFilter<string>(search, property, values[key], 'zips');

          if (isDefined(scf) && isNotEmpty(scf)) {
            const { type } = scf;

            switch (type) {
              case 'exclude':
                property = 'excludeScf';
                break;

              case 'include':
                property = 'includeScf';
                break;
            }

            processFilter<string>(search, property, values[key], 'scf');
          }
        }
        break;
      }

      case SearchType.Omits: {
        processFilter<IOmitFlag>(search, 'omitFlags', values, key);
        break;
      }

      case SearchType.HouseOrder: {
        processFilter<IHouseOrder>(search, 'excludePrevHouseOrder', values, key);
        break;
      }

      case SearchType.MailCodes: {
        const selectedFilter = get(values, key) as ISelectedFilter<IMailCode>;

        if (isNotDefined(selectedFilter) || isEmpty(selectedFilter)) {
          break;
        }

        const { type } = selectedFilter;
        let property: keyof IAudienceSearch;

        switch (type) {
          case 'exclude':
            property = 'excludeMailCode';
            break;

          case 'include':
            property = 'includeMailCode';
            break;
        }

        processFilter<IMailCode>(search, property, values, key);
        break;
      }

      case SearchType.ListRentals: {
        processFilter<IListRental>(search, 'excludePrevNLSOrder', values, key);
        break;
      }

      case SearchType.Tags: {
        const tags = values[key];

        if (isNotDefined(tags) || isEmpty(tags)) {
          break;
        }

        const clientTags = tags.clientTags as ISelectedFilter<ITag>;
        const mailCodeTags = tags.mailCodeTags as ISelectedFilter<ITag>;
        const packageTags = tags.packageTags as ISelectedFilter<ITag>;

        if (isDefined(clientTags) && isNotEmpty(clientTags)) {
          processFilter<ITag>(search, 'includeClientTags', tags, 'clientTags');
        }
        if (isDefined(packageTags) && isNotEmpty(packageTags)) {
          processFilter<ITag>(search, 'includePackageTags', tags, 'packageTags');
        }
        if (isDefined(mailCodeTags) && isNotEmpty(mailCodeTags)) {
          processFilter<ITag>(search, 'includeMailingTags', tags, 'mailCodeTags');
        }
      }
    }
  }
};

const generateMiscellaneousSearchParameters = (search: IAudienceSearch, fieldValues: FieldValues) => {
  if (isDefined(fieldValues)) {
    const keys = Object.keys(fieldValues);

    for (const key of keys) {
      const formValue = fieldValues[key];

      if (isNotDefined(formValue) || isEmpty(formValue)) {
        continue;
      }

      const { value } = formValue;

      if (key == 'includeNonDonor') {
        search = Object.assign(search, { includeNonDonor: isEmpty(value) ? null : value });
      } else {
        search = Object.assign(search, { [key]: value === '1' });
      }
    }
  }
};

const generateSequenceSearchParameters = (): IAudienceCategorySearch => {
  return { and: [], or: [], step: '1' };
};

const processFilter = <TSelectedFilterType>(
  search: IAudienceSearch,
  property: keyof IAudienceSearch,
  fieldValues: FieldValues,
  filterType: FilterType | string
) => {
  const selectedFilter = fieldValues[filterType] as ISelectedFilter<TSelectedFilterType>;

  if (isNotDefined(selectedFilter) || isEmpty(selectedFilter)) {
    return;
  }

  const { filters } = selectedFilter;

  if (isNotDefined(filters) || isEmpty(filters)) {
    return;
  }

  search[property] = filters as never;
};

export const generateSearchParameters = (
  searchType: SearchType,
  fieldValues: FieldValues,
  clientLists: IClientListReturn,
  categories?: IFormCategory[]
): IAudienceSearch => {
  const search: IAudienceSearch = {
    categoryJson: null,
  };

  switch (searchType) {
    case SearchType.Basic:
      generateBasicSearchParameters(search, fieldValues, categories);
      break;

    case SearchType.Conditions:
      generateConditionsSearchParameters(search, fieldValues);
      break;

    case SearchType.Sequences:
      generateSequenceSearchParameters();
      break;
  }

  generateFilterParameters(search, fieldValues);

  generateAttributesParameters(search, fieldValues);

  generateDataSourceParameters(search, fieldValues, clientLists);

  return search;
};
